import {
  AdviceProductChangeType,
  InsuranceAdvice,
  PurchaseRecommendation,
  UserAuthorizationLevel,
} from "Store/advice/interfaces";
import {
  getAdviceRestrictions,
  getCorrectPaymentValue,
  isCustomerOver50AndCanNotHaveOTP,
  isOneTimePaymentAllowed as isOneTimePaymentAllowedCheck,
  replaceUserSupportNumberInText,
} from "./utils";

import { StyledAlert } from "App/global-style";
import { VALUE_TYPE } from "Components/NumberValue";
import { formatToLocaleString } from "Formatting/formatNumber";
import { useIsAdvisorSelector } from "Store/advisor/advisorSelectors";
import { usePartnerId } from "Store/partners/partnerHooks";
import { FormikProps } from "formik";
import React from "react";
import { AdviceProductDetailsFormValues } from "../../ProductDetails/FormElements/interfaces";
import { DisabilityOneTimePayment } from "./DisabilityOneTimePayment";
import { DisabilityOneTimePaymentAdvisor } from "./DisabilityOneTimePaymentAdvisor/DisabilityOneTimePaymentAdvisor";
import { AdvicePriceFormValues } from "./interfaces";

interface Props {
  insuranceAdvice: InsuranceAdvice;
  formProps: FormikProps<AdvicePriceFormValues>;
  adviceProductIntroFormValues: AdviceProductDetailsFormValues;
  oneTimePaymentValue: number;
  adviceCard: any;
  lang: string;
  handleCustomerChange: (
    changeType: AdviceProductChangeType,
    changeValue: number | boolean,
  ) => void;
  priceIsLoading: boolean;
}

interface PurchaseFromAdvisorAlertProps {
  readonly partnerId: string;
  readonly customMaximumPayoutText: string;
  readonly customText: string;
  readonly purchaseFromAdvisor: any;
  readonly lang: string;
}

const PurchaseFromAdvisorAlert = ({
  partnerId,
  customMaximumPayoutText,
  customText,
  purchaseFromAdvisor,
  lang,
}: PurchaseFromAdvisorAlertProps): React.ReactElement => {
  const weAdvice: string = purchaseFromAdvisor.weAdvice[lang]
    .replace("{{payment}}", customText)
    .replace("{{isMaximumPayout}}", customMaximumPayoutText);
  return (
    <StyledAlert
      className="PurchaseFromAdvisorText"
      title={replaceUserSupportNumberInText(
        partnerId,
        purchaseFromAdvisor.userSupportNumber,
        weAdvice,
        lang,
      )}
    />
  );
};

export const DisabilityAdviceCardForm = ({
  insuranceAdvice,
  adviceProductIntroFormValues,
  oneTimePaymentValue,
  adviceCard,
  lang,
  handleCustomerChange,
  priceIsLoading,
}: Props): React.ReactElement | null => {
  const isAdvisor = useIsAdvisorSelector();
  const partnerId = usePartnerId();

  const { singlePayment, yearlyPayment } = insuranceAdvice;

  const shouldPurchaseFromAdvisor =
    !isAdvisor &&
    yearlyPayment?.recommendationConfiguration.recommendation ===
      PurchaseRecommendation.PURCHASE_FROM_ADVISOR;
  const shouldPurchaseFromAdvisorOneTimePayment =
    singlePayment?.recommendationConfiguration.recommendation ===
    PurchaseRecommendation.PURCHASE_FROM_ADVISOR;
  const purchaseFromAdvisorValue = getCorrectPaymentValue(
    UserAuthorizationLevel.PRODUCT_MANAGER,
    yearlyPayment,
  );
  const purchaseFromAdvisorOneTimeValue = getCorrectPaymentValue(
    UserAuthorizationLevel.PRODUCT_MANAGER,
    singlePayment,
  );

  const { purchaseFromAdvisor } = adviceCard.adviceUnavailableMessages;
  const { oneTime, yearly, andOneTime } = purchaseFromAdvisor.payment;
  const pensionPaymentText = `${formatToLocaleString(
    purchaseFromAdvisorValue,
  )} ${VALUE_TYPE.NOK}`;
  const capitalPaymentText = `${formatToLocaleString(
    purchaseFromAdvisorOneTimeValue,
  )} ${VALUE_TYPE.NOK}`;
  const restrictions = getAdviceRestrictions(insuranceAdvice);
  const disabilityOTPRestrictions = getAdviceRestrictions(
    insuranceAdvice,
    true,
  );
  const isOneTimePaymentAllowed = isOneTimePaymentAllowedCheck(singlePayment);

  if (isAdvisor) {
    return (
      <DisabilityOneTimePaymentAdvisor
        adviceProductIntroFormValues={adviceProductIntroFormValues}
        oneTimePaymentValue={oneTimePaymentValue}
        oneTimePayment={adviceCard.disabilityOneTimePayment}
        lang={lang}
        handleCustomerChange={handleCustomerChange}
        priceIsLoading={priceIsLoading}
        adviceRestrictions={disabilityOTPRestrictions}
        eligibleForPaymentAfter3Months={
          yearlyPayment?.details?.eligibleForPaymentAfter3Months
        }
        customerOver50AndCanNotHaveOTP={isCustomerOver50AndCanNotHaveOTP(
          singlePayment,
        )}
      />
    );
  }

  return (
    <>
      {shouldPurchaseFromAdvisor &&
        !shouldPurchaseFromAdvisorOneTimePayment && (
          <PurchaseFromAdvisorAlert
            partnerId={partnerId}
            customMaximumPayoutText={
              purchaseFromAdvisorValue === restrictions.maxProductManager
                ? purchaseFromAdvisor.isMaximumPayout[lang]
                : ""
            }
            customText={yearly[lang].replace("{{value}}", pensionPaymentText)}
            purchaseFromAdvisor={purchaseFromAdvisor}
            lang={lang}
          />
        )}
      {isOneTimePaymentAllowed && oneTimePaymentValue > 0 && (
        <>
          <DisabilityOneTimePayment
            adviceProductIntroFormValues={adviceProductIntroFormValues}
            oneTimePaymentValue={oneTimePaymentValue}
            oneTimePayment={adviceCard.disabilityOneTimePayment}
            lang={lang}
            handleCustomerChange={handleCustomerChange}
          />
          {shouldPurchaseFromAdvisorOneTimePayment &&
            !shouldPurchaseFromAdvisor && (
              <PurchaseFromAdvisorAlert
                partnerId={partnerId}
                customMaximumPayoutText={
                  purchaseFromAdvisorOneTimeValue ===
                  disabilityOTPRestrictions.maxProductManager
                    ? purchaseFromAdvisor.isMaximumPayoutOneTime[lang]
                    : ""
                }
                customText={oneTime[lang].replace(
                  "{{value}}",
                  capitalPaymentText,
                )}
                purchaseFromAdvisor={purchaseFromAdvisor}
                lang={lang}
              />
            )}
        </>
      )}
      {shouldPurchaseFromAdvisor && shouldPurchaseFromAdvisorOneTimePayment && (
        <PurchaseFromAdvisorAlert
          partnerId={partnerId}
          customMaximumPayoutText={
            purchaseFromAdvisorValue === restrictions.maxProductManager
              ? purchaseFromAdvisor.isMaximumPayout[lang]
              : ""
          }
          customText={`${yearly[lang].replace(
            "{{value}}",
            pensionPaymentText,
          )} ${andOneTime[lang].replace("{{value}}", capitalPaymentText)}`}
          purchaseFromAdvisor={purchaseFromAdvisor}
          lang={lang}
        />
      )}
    </>
  );
};
